// useDCAState.js - Custom hook for DCA bot state management
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { getSafeNumericValue, isInProgressDecimal } from "../utils";
import { initSettingDCA } from "../components/drawers/DrawerActivateBot";

export const useDCAState = ({
  userBalance,
  selectedBot,
  formDCA,
  setFormDCA,
  setSelectedExchange,
  selectedExchange,
  setSelectedPair,
  selectedPair,
  openDialogBot,
  selectedSetting,
  setSelectedSetting,
  setErrors,
  errors,
  actualUserBalance,
}) => {
  const baseCapital = Number(userBalance) || 0;
  const strategy = selectedBot?.bot_name?.toLowerCase();

  const [activeIndex, setActiveIndex] = useState(0);
  const [capitalInput, setCapitalInput] = useState("");
  const [percentage, setPercentage] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initialEditCapital, setInitialEditCapital] = useState(0);
  const [systemSettingData, setSystemSettingData] = useState(null);

  // Track previous exchange/pair to detect actual changes
  const prevExchangeRef = useRef(null);
  const prevPairRef = useRef(null);

  // Calculate maximum allowed capital based on mode (edit mode)
  const maxCapital = useMemo(() => {
    // if (openDialogBot === "step-bot-2-edit") {
    //   return actualUserBalance + initialEditCapital;
    // }
    return actualUserBalance;
  }, [openDialogBot, actualUserBalance, initialEditCapital]);

  // Initialize capitalInput from form data (for EDIT mode)
  // useEffect(() => {
  //   if (openDialogBot === "step-bot-2-edit" && !isInitialized) {
  //     const capital = formDCA?.settings?.capital;

  //     if (capital && Number(capital) > 0) {
  //       const numCapital = Number(capital);
  //       setCapitalInput(String(capital));
  //       setInitialEditCapital(numCapital);
  //       setIsInitialized(true);
  //     }
  //   }
  // }, [openDialogBot, formDCA?.settings?.capital, isInitialized]);

  // Calculate percentage for edit mode
  // useEffect(() => {
  //   if (openDialogBot === "step-bot-2-edit") {
  //     const numCapital = Number(formDCA?.settings?.capital);
  //     setPercentage((numCapital / maxCapital) * 100);
  //   }
  // }, [maxCapital]);

  const handleCapitalChange = useCallback(
    (e) => {
      let value = e.target.value;

      value = value.replace(",", ".");

      if (!/^\d*\.?\d*$/.test(value)) return;

      let num = parseFloat(value);
      if (isNaN(num)) num = 0;

      if (num > maxCapital) num = maxCapital;

      // keep user's typed format (with dot only)
      setCapitalInput(value);

      // update percentage
      if (maxCapital > 0) {
        setPercentage((num / maxCapital) * 100);
      }

      // update form
      setFormDCA((prev) => ({
        ...prev,
        settings: { ...(prev?.settings || {}), capital: num },
      }));
    },
    [maxCapital, setFormDCA]
  );

  const handleCapitalBlur = useCallback(() => {
    let numericValue = getSafeNumericValue(capitalInput);
    const min = 5;
    const max = maxCapital;

    if (numericValue < min) numericValue = min;
    if (numericValue > max) numericValue = max;

    setCapitalInput(numericValue.toString());

    setFormDCA((prev) => ({
      ...prev,
      settings: { ...(prev?.settings || {}), capital: numericValue },
    }));
  }, [capitalInput, maxCapital, setFormDCA]);

  const handleSliderChange = useCallback(
    (newPercentage) => {
      let newCapital = ((newPercentage / 100) * maxCapital).toFixed(2);
      let numericValue = parseFloat(newCapital);

      // If editing, don't allow below initial capital
      // if (
      //   openDialogBot === "step-bot-2-edit" &&
      //   numericValue < initialEditCapital
      // ) {
      //   numericValue = initialEditCapital;
      //   newPercentage = (numericValue / maxCapital) * 100;
      // }

      setPercentage(newPercentage);
      setCapitalInput(numericValue.toString());

      const tradeSize = parseFloat(formDCA?.settings?.amount || 0);
      if (Number(tradeSize) > Number(numericValue)) {
        setFormDCA((prev) => ({
          ...prev,
          settings: { ...(prev?.settings || {}), amount: numericValue },
        }));
      }
    },
    [maxCapital, formDCA, setFormDCA, openDialogBot, initialEditCapital]
  );

  const handleChangeTradeSize = useCallback(
    (value) => {
      let numericValue = value.replace(/[^0-9.]/g, "");
      const capital = parseFloat(formDCA?.settings?.capital || 0);

      if (!isInProgressDecimal(numericValue)) {
        numericValue = getSafeNumericValue(numericValue, capital);
      }

      setFormDCA((prev) => ({
        ...prev,
        settings: { ...(prev?.settings || {}), amount: numericValue },
      }));
    },
    [formDCA, setFormDCA]
  );

  // Sync capitalInput → form (for CREATE mode and user changes)
  useEffect(() => {
    // if (openDialogBot === "step-bot-2-edit" && !isInitialized) return;
    if (openDialogBot === "step-bot-3-edit") return;

    const capital = Number(capitalInput);
    if (!capitalInput || capitalInput === "") return;

    setFormDCA((prev) => {
      if (prev?.settings?.capital !== capital) {
        return {
          ...prev,
          settings: { ...(prev?.settings || {}), capital: capital },
        };
      }
      return prev;
    });
  }, [capitalInput, openDialogBot, isInitialized, setFormDCA]);

  // Reset form when exchange/pair actually changes (not when navigating steps)
  useEffect(() => {
    // Skip in edit mode
    if (openDialogBot === "step-bot-2-edit") return;

    // Check if exchange or pair actually changed
    const exchangeChanged =
      prevExchangeRef.current !== null &&
      prevExchangeRef.current?.id !== selectedExchange?.id;
    const pairChanged =
      prevPairRef.current !== null &&
      prevPairRef.current?.id !== selectedPair?.id;

    // Update refs
    prevExchangeRef.current = selectedExchange;
    prevPairRef.current = selectedPair;

    // Only reset if there was an actual change (not initial mount or step navigation)
    if (exchangeChanged || pairChanged) {
      setCapitalInput("");
      setPercentage(0);

      setFormDCA((prev) => ({
        ...(prev || {}),
        settings: {
          ...initSettingDCA,
        },
      }));
    }
  }, [selectedExchange, selectedPair, openDialogBot, setFormDCA]);

  // Reset when drawer closes
  useEffect(() => {
    if (!openDialogBot) {
      setSelectedSetting(null);
      setActiveIndex(0);
      setSelectedExchange(null);
      setSelectedPair(null);
      setCapitalInput("");
      setPercentage(0);
      setErrors({});
      setIsInitialized(false);
      setInitialEditCapital(0);
      setSystemSettingData(null);
      prevExchangeRef.current = null;
      prevPairRef.current = null;
    }
  }, [
    openDialogBot,
    setSelectedExchange,
    setSelectedPair,
    setErrors,
    setSelectedSetting,
  ]);

  // Reset when going back to step 1
  useEffect(() => {
    if (openDialogBot === "step-bot-1") {
      setSelectedSetting(null);
      setActiveIndex(0);
      setSelectedExchange(null);
      setSelectedPair(null);
      setCapitalInput("");
      setPercentage(0);
      setErrors({});
      setIsInitialized(false);
      setInitialEditCapital(0);
      setSystemSettingData(null);
      prevExchangeRef.current = null;
      prevPairRef.current = null;
    }
  }, [
    openDialogBot,
    setSelectedExchange,
    setSelectedPair,
    setErrors,
    setSelectedSetting,
  ]);

  // Set custom mode when opening edit
  useEffect(() => {
    if (openDialogBot === "step-bot-2-edit") {
      setSelectedSetting("custom");
    }
  }, [openDialogBot, setSelectedSetting]);

  // Reset settings when switching modes (but not in edit or during navigation)
  useEffect(() => {
    if (!selectedSetting) return;
    if (
      openDialogBot === "step-bot-2-edit" ||
      openDialogBot === "step-bot-2" ||
      openDialogBot === "step-bot-3-edit" ||
      openDialogBot === "step-bot-3"
    )
      return;

    setActiveIndex(0);
    setFormDCA((prev) => ({
      ...(prev || {}),
      settings: {
        ...initSettingDCA,
        capital: prev?.settings?.capital,
      },
    }));
  }, [selectedSetting, openDialogBot, setFormDCA]);

  useEffect(() => {
    setErrors({});
  }, [selectedSetting, setErrors]);

  // Restore capital when returning to step 2
  // useEffect(() => {
  //   if (openDialogBot === "step-bot-2") {
  //     const capt = formDCA?.settings?.capital;
  //     if (capt && Number(capt) > 0) {
  //       setCapitalInput(String(capt));
  //       const numCapital = Number(capt);
  //       const totalBalance = actualUserBalance + numCapital;
  //       if (totalBalance > 0) {
  //         setPercentage((numCapital / totalBalance) * 100);
  //       }
  //     }
  //   }
  // }, [openDialogBot, formDCA?.settings?.capital, actualUserBalance]);

  return {
    strategy,
    baseCapital,
    activeIndex,
    setActiveIndex,
    selectedSetting,
    setSelectedSetting,
    capitalInput,
    percentage,
    handleCapitalChange,
    handleCapitalBlur,
    handleSliderChange,
    handleChangeTradeSize,
    setSystemSettingData,
  };
};
